/**
 * The rest of the Aerodrome menu: reporting back for duty, REQUEST TRANSFER,
 * and PERSONAL AIRCRAFT.
 *
 * Every gate here is a verbatim `PS.EXE` string with its slots filled
 * (docs/game/campaign.md §§ *Squadron transfers*, *Personal aircraft*):
 *
 * - `"You must attain the rank of %b before the %s will accept a transfer request."`
 * - `"Due to your recent transfer, the %s is denying your request. Spend at least four months with %q before transferring."`
 * - `"You have been transferred to %q stationed at %p."`
 * - `"You must attain the rank of %c before the %s will provide you with a personal aircraft."`
 * - `"Request Denied! The [squadron] recently supplied you with a personal aircraft. Try it out for at least three months before getting a new aircraft."`
 *
 * Two things are port-side and marked: **which** rank each kind of squadron
 * demands (the strings have a slot, not a value — this port asks for the top
 * rank for an elite posting and flight-leader rank for an ordinary one, which
 * matches the manual's framing of elite squadrons as something you earn), and
 * the fact that a squadron never turns you down for any other reason.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import { frontLineScouts } from "./aircraft-service.ts";
import { WAR_END_DATE, isOnOrAfter, monthsBetween, type CareerDate } from "./calendar.ts";
import { appendLog, currentAircraft, squadronOf, type CareerState, type PaintColor } from "./career-state.ts";
import { FLIGHT_LEADER_RANK, SQUADRON_COMMAND_RANK, SERVICE_LABELS, rankOf, serviceFor } from "./service.ts";
import { isActiveOn, isPostable, SQUADRONS, requireSquadron, type Squadron } from "./squadrons.ts";

/** Verbatim: four months with a squadron before another transfer. */
export const TRANSFER_TENURE_MONTHS = 4;
/** Verbatim: three months with a personal aircraft before requesting another. */
export const AIRCRAFT_TENURE_MONTHS = 3;

function retireIfWarOver(career: CareerState, date: CareerDate): CareerState {
  if (!isOnOrAfter(date, WAR_END_DATE)) return career;
  return { ...career, date, status: "retired", dutyReturnDate: undefined };
}

/**
 * Back on duty after a hospital stay or a prison camp. The lost time is spent
 * here, in one step, which is the "time passing" the manual describes: *"time
 * that could be used to score victories will be lost"*.
 */
export function resumeDuty(career: CareerState): CareerState {
  if (career.status !== "hospital" && career.status !== "pow") return career;
  const date = career.dutyReturnDate ?? career.date;
  const back = career.status === "pow" ? "You made it back across the lines and rejoined the squadron." : "Passed fit to fly.";
  const resumed: CareerState = { ...career, date, status: "active", dutyReturnDate: undefined };
  return appendLog(retireIfWarOver(resumed, date), back, date);
}

export interface TransferOption {
  readonly squadron: Squadron;
  readonly eligible: boolean;
  /** Empty when eligible; the squadron's own refusal otherwise. */
  readonly refusal: string;
}

function requiredRank(unit: Squadron): number {
  return unit.elite ? SQUADRON_COMMAND_RANK : FLIGHT_LEADER_RANK;
}

function refusalFor(career: CareerState, unit: Squadron): string {
  const needed = requiredRank(unit);
  if (career.rank < needed) {
    return `You must attain the rank of ${rankOf(career.nation, needed).title} before ${unit.name} will accept a transfer request.`;
  }
  const tenure = monthsBetween(career.postedOn, career.date);
  if (tenure < TRANSFER_TENURE_MONTHS) {
    return `Due to your recent transfer, ${unit.name} is denying your request. Spend at least four months with ${squadronOf(career).name} before transferring.`;
  }
  return "";
}

/** Every squadron this pilot could ask for, with the answer he would get. */
export function transferOptions(career: CareerState): readonly TransferOption[] {
  return SQUADRONS.filter(
    (unit) => unit.id !== career.squadronId && isPostable(unit, career.nation) && isActiveOn(unit, career.date),
  ).map((unit) => {
    const refusal = refusalFor(career, unit);
    return { squadron: unit, eligible: refusal === "", refusal };
  });
}

/** Moves the pilot, or throws with the refusal the squadron would have given. */
export function requestTransfer(career: CareerState, squadronId: string): CareerState {
  const unit = requireSquadron(squadronId);
  const refusal = refusalFor(career, unit);
  if (refusal !== "") throw new Error(refusal);
  const moved: CareerState = {
    ...career,
    squadronId: unit.id,
    aerodrome: unit.aerodrome,
    region: unit.region,
    postedOn: career.date,
  };
  return appendLog(moved, `You have been transferred to ${unit.name} stationed at ${unit.aerodrome}.`);
}

export interface AircraftRequest {
  readonly allowed: boolean;
  /** The offer, or the refusal, in the squadron's own words. */
  readonly message: string;
  /** The types on offer, newest last. Empty when the request is refused. */
  readonly offered: readonly AircraftKey[];
}

/**
 * PERSONAL AIRCRAFT → REQUEST NEW AIRCRAFT. The offer wording is `PS.EXE`'s,
 * with its "distinquished" typo corrected; the trade wording is its second
 * variant, used once the pilot already has a machine of his own.
 */
export function personalAircraftRequest(career: CareerState): AircraftRequest {
  const unit = squadronOf(career);
  const service = SERVICE_LABELS[serviceFor(career.nation, career.date)];
  if (career.rank < SQUADRON_COMMAND_RANK) {
    const needed = rankOf(career.nation, SQUADRON_COMMAND_RANK).title;
    return {
      allowed: false,
      message: `You must attain the rank of ${needed} before the ${service} will provide you with a personal aircraft.`,
      offered: [],
    };
  }
  const held = career.personalAircraft;
  if (held && monthsBetween(held.issuedOn, career.date) < AIRCRAFT_TENURE_MONTHS) {
    return {
      allowed: false,
      message: `Request Denied! ${unit.name} recently supplied you with a personal aircraft. Try it out for at least three months before getting a new aircraft.`,
      offered: [],
    };
  }
  const offered = frontLineScouts(career.nation, career.date);
  const message = held
    ? `Due to your distinguished service, ${unit.name} has offered you a trade for your personal fighter.`
    : `Due to your distinguished service, ${unit.name} has offered you the use of a personal aircraft.`;
  return { allowed: true, message, offered };
}

/** Takes up the offer. Throws if the squadron would have refused it. */
export function requestPersonalAircraft(career: CareerState, aircraft: AircraftKey): CareerState {
  const request = personalAircraftRequest(career);
  if (!request.allowed) throw new Error(request.message);
  if (!request.offered.includes(aircraft)) {
    throw new Error(`${aircraft} is not on offer at ${squadronOf(career).name} in this month of the war`);
  }
  const previous = career.personalAircraft;
  const issued = {
    aircraft,
    wings: previous?.wings,
    fuselage: previous?.fuselage,
    issuedOn: career.date,
  };
  return appendLog({ ...career, personalAircraft: issued }, `Personal aircraft: ${aircraft}.`);
}

export interface PaintChoice {
  readonly wings?: PaintColor;
  readonly fuselage?: PaintColor;
}

/**
 * Whether this pilot may repaint, and why not if he may not.
 *
 * The original gates paint on **one** thing: the personal aircraft, which is
 * itself gated on rank. *"After reaching the rank of Captain or Rittmeister,
 * you will be given the use of a personal aircraft. On the Personal Aircraft
 * screen you may view your plane, request a new aircraft and paint your
 * plane."* (manual, "Personal Aircraft"). Victories, squadron command beyond
 * that rank, and medals are **not** gates.
 *
 * Nor is nationality, deliberately. The Designer Notes are explicit that the
 * historical asymmetry was dropped: *"Finally, we allow the player to paint
 * his aircraft as a British pilot. While it was the custom of the German
 * pilots to do so, the R.F.C. would never allow their pilots to indulge in
 * such a flamboyant act. We allow it because it's fun."* The disassembly
 * agrees — the player's paint path (`RB.EXE` seg `133f:0123`, the
 * `planeRec[0x7C] == 4` branch) reads six slots and has no side dimension at
 * all.
 */
export function paintEligibility(career: CareerState): { readonly allowed: boolean; readonly message: string } {
  if (career.personalAircraft) return { allowed: true, message: "" };
  const needed = rankOf(career.nation, SQUADRON_COMMAND_RANK).title;
  const service = SERVICE_LABELS[serviceFor(career.nation, career.date)];
  return {
    allowed: false,
    message: `You must attain the rank of ${needed} before the ${service} will provide you with a personal aircraft.`,
  };
}

/** PAINT AIRCRAFT → PAINT WHAT? (wings, fuselage, everything) → WHICH COLOR? */
export function paintAircraft(career: CareerState, choice: PaintChoice): CareerState {
  const held = career.personalAircraft;
  if (!held) throw new Error(paintEligibility(career).message);
  const wings = choice.wings ?? held.wings;
  const fuselage = choice.fuselage ?? held.fuselage;
  if (wings === held.wings && fuselage === held.fuselage) return career;
  return { ...career, personalAircraft: { ...held, wings, fuselage } };
}

/** `"Fokker Dr.I — wings Blue, fuselage Black"`, or the plain type if unpainted. */
export function personalAircraftLabel(career: CareerState): string | undefined {
  const held = career.personalAircraft;
  if (!held) return undefined;
  const paint = [held.wings ? `wings ${held.wings}` : "", held.fuselage ? `fuselage ${held.fuselage}` : ""]
    .filter((part) => part !== "")
    .join(", ");
  return paint === "" ? currentAircraft(career) : `${currentAircraft(career)} — ${paint}`;
}
